<template>
  <div v-if="hasFindings" class="full-details-wrapper">
    <div class="d-flex align-center px-4 pt-4">
      <div class="subtitle1 mb-4" data-testid="tickets-page-findings-title">
        {{ $t("ticketDetails.findings") }}
      </div>
    </div>

    <unified-email-dlp-expansion-panels :grouped-findings="groupedFindings" :ticket="ticket" />
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, type PropType } from "vue";
import type { Attachment, Finding, TicketDetails } from "@/_store/tickets/tickets.module";
import { FindingDetectedInType } from "@/_store/tickets/tickets.module";
import { TicketTrigger } from "@/constants/tickets.ts";
import UnifiedEmailDlpExpansionPanels from "@/components/tickets/ticket-preview-details/base-components/UnifiedEmailDlpExpansionPanels.vue";
import { sortFindingsGroupsByDataType } from "@/_helpers/ticket-helpers.ts";

export interface DlpSourceFinding {
  source: FindingDetectedInType;
  fileName?: string;
  fileSize?: number;
  findings: Finding[];
}

export interface GroupedDlpFindings {
  eventTrigger: TicketTrigger;
  numberOfFindings: number;
  sources: DlpSourceFinding[];
}

interface ExtendedFinding extends Finding {
  source: FindingDetectedInType;
  eventTrigger: TicketTrigger;
  fileName: string;
}

export default defineComponent({
  components: {
    UnifiedEmailDlpExpansionPanels,
  },
  props: {
    ticket: {
      type: Object as PropType<TicketDetails>,
      required: true,
    },
  },
  setup(props) {
    const findings = computed(() => (props.ticket?.sections?.findings || []) as ExtendedFinding[]);

    const attachments = computed(
      () => (props.ticket?.sections?.emailMetaData?.attachments || []) as Attachment[]
    );

    const attachmentSizeMap = computed(() => {
      const map: Record<string, number> = {};
      attachments.value.forEach((attachment) => {
        map[attachment.fileName] = attachment.fileSize;
      });
      return map;
    });

    const hasFindings = computed(() => findings.value.length > 0);

    const groupFindingsByTrigger = (): Record<string, ExtendedFinding[]> => {
      const triggerGroups: Record<string, ExtendedFinding[]> = {};
      findings.value.forEach((finding) => {
        const trigger = finding.eventTrigger;
        if (!triggerGroups[trigger]) {
          triggerGroups[trigger] = [];
        }
        triggerGroups[trigger].push(finding);
      });
      return triggerGroups;
    };

    const groupFindingsBySource = (
      trigger: string,
      triggerFindings: ExtendedFinding[]
    ): GroupedDlpFindings => {
      const sourceGroups: Record<string, DlpSourceFinding> = {};

      triggerFindings.forEach((finding) => {
        const source = finding.source;
        const isAttachment = source === FindingDetectedInType.ATTACHMENT;
        const groupKey = isAttachment ? `${source}_${finding.filePath}` : source;

        if (!sourceGroups[groupKey]) {
          const fileName = finding.filePath;
          sourceGroups[groupKey] = {
            source,
            fileName: isAttachment ? fileName : undefined,
            fileSize: isAttachment ? (attachmentSizeMap.value[fileName || ""] ?? 0) : undefined,
            findings: [],
          };
        }
        sourceGroups[groupKey].findings.push(finding);
      });

      return {
        eventTrigger: trigger as TicketTrigger,
        numberOfFindings: triggerFindings.length,
        sources: Object.values(sourceGroups),
      };
    };

    const groupedFindings = computed((): GroupedDlpFindings[] => {
      const triggerGroups = groupFindingsByTrigger();
      return sortFindingsGroupsByDataType(
        Object.entries(triggerGroups).map(([trigger, triggerFindings]) =>
          groupFindingsBySource(trigger, triggerFindings)
        )
      );
    });

    return {
      hasFindings,
      groupedFindings,
    };
  },
});
</script>

<style scoped lang="scss">
.full-details-wrapper {
  border: 1px solid rgb(var(--v-theme-indigo-faint));
}

.expansion-panel-header {
  height: 56px;
  background-color: rgb(var(--v-theme-indigo-faint));
  border-radius: 0 !important;
}

:deep(*) {
  .v-table__wrapper {
    border: 0 !important;
  }

  .v-expansion-panel--active:not(:first-child),
  .v-expansion-panel--active + .v-expansion-panel {
    margin-top: 0 !important;
  }
}
</style>
